import { Fragment, useMemo, type ReactNode } from 'react';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn, getColorForText } from '@/lib/utils';
import { AVATAR_COLORS } from '@/constants/avatar-colors';
import { TranslationNS } from '@gokrew/models/constants';
import type { CalendarLeave } from '@gokrew/models/leave-management';
import type { CalendarEvent } from '@/types/leave-calendar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip';
import { EventDetailCard } from './EventDetailCard';
import styles from './LeaveTimeline.module.css';

/** A person to keep a row for, whether or not they have leave this month. */
export interface TimelineEmployee {
  id: string;
  name: string;
}

interface LeaveTimelineProps {
  events?: CalendarEvent[];
  /** Day indices (0 = Sunday) that are not working days. */
  nonWorkingDays?: number[];
  selectedMonth: number;
  selectedYear: number;
  /**
   * The roster to show. Rows render for everyone here even with no leave, so
   * the timeline reads as "nobody is away" rather than collapsing to an empty
   * state. Omit it and the rows are derived from whoever has leave.
   */
  employees?: TimelineEmployee[];
  /**
   * Under the last row, e.g. a `LoadMoreSentinel` when the roster is paged:
   * a company can have more people than one page.
   */
  footer?: ReactNode;
}

/** Local parts only — `toISOString` shifts the date across midnight. */
const toIso = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Squared-off on a continued edge, rounded on a terminal one. */
const radius = (contLeft: boolean, contRight: boolean) => {
  const r = 'var(--radius-md)';
  const f = '0.125rem';
  return `${contLeft ? f : r} ${contRight ? f : r} ${contRight ? f : r} ${contLeft ? f : r}`;
};

const initialsOf = (name: string) => {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '';
  return (w.length === 1 ? w[0].slice(0, 2) : w[0][0] + w[1][0]).toUpperCase();
};

/** Two-letter marker used when a bar is too narrow for the full name. */
const shortCode = (name: string) => {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '';
  return (w.length === 1 ? w[0].slice(0, 2) : w[0][0] + w[1][0]).toUpperCase();
};

interface Segment {
  /** Inclusive day-index range within the month. */
  a: number;
  b: number;
}

/**
 * Splits a request's run across the month into working stretches (bars) and
 * the non-working gaps between them (dashed connectors). Leading and trailing
 * non-working days are trimmed, so a request never starts or ends on a dash.
 */
const buildSegments = (
  days: Date[],
  isWorkingDay: (d: Date) => boolean,
  startIso: string,
  endIso: string
): { segs: Segment[]; conns: Segment[] } | null => {
  const within = days
    .map((d, i) => ({ i, iso: toIso(d), working: isWorkingDay(d) }))
    .filter((x) => x.iso >= startIso && x.iso <= endIso);

  if (!within.some((x) => x.working)) return null;

  let s = 0;
  let e = within.length - 1;
  while (!within[s].working) s++;
  while (!within[e].working) e--;

  const segs: Segment[] = [];
  const conns: Segment[] = [];
  let cur: (Segment & { working: boolean }) | null = null;

  within.slice(s, e + 1).forEach((x) => {
    if (cur && cur.working === x.working && cur.b === x.i - 1) {
      cur.b = x.i;
      return;
    }
    cur = { working: x.working, a: x.i, b: x.i };
    (x.working ? segs : conns).push(cur);
  });

  return { segs, conns };
};

interface TimelineRequest {
  key: string;
  leave: CalendarLeave;
  className?: string;
  rangeStart: string;
  rangeEnd: string;
}

export function LeaveTimeline({
  events = [],
  nonWorkingDays = [],
  selectedMonth,
  selectedYear,
  employees,
  footer,
}: LeaveTimelineProps) {
  const { t, i18n } = useTranslation(TranslationNS.LeaveManagement);
  const todayIso = toIso(new Date());

  // The app's language, not the browser's — otherwise the timeline's weekdays
  // disagree with the month grid's on an English browser set to French.
  const weekdayFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }),
    [i18n.language]
  );
  const weekdayLabel = (d: Date) => weekdayFormatter.format(d).slice(0, 2);

  const days = useMemo(() => {
    const count = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    return Array.from(
      { length: count },
      (_, i) => new Date(selectedYear, selectedMonth, i + 1)
    );
  }, [selectedYear, selectedMonth]);

  const holidays = useMemo(() => {
    const map = new Map<string, string>();
    events.forEach((ev) => {
      if (ev.extendedProps.type === 'holiday') {
        map.set(ev.start.slice(0, 10), ev.extendedProps.label);
      }
    });
    return map;
  }, [events]);

  const isWeekend = (d: Date) => nonWorkingDays.includes(d.getDay());
  const isWorkingDay = (d: Date) => !isWeekend(d) && !holidays.has(toIso(d));

  /**
   * One entry per request. `useLeaveCalendarBase` emits a separate event per
   * contiguous span, so several events can share a request — regroup on the id
   * and use the request's own range, which is not clamped to the window.
   */
  const rows = useMemo(() => {
    const requests = new Map<string, TimelineRequest>();

    events.forEach((ev) => {
      const { type, leave, leaveRange, className } = ev.extendedProps;
      if (type !== 'leave' || !leave) return;

      const key = leave.leaveRequestId || ev.id;
      if (requests.has(key)) return;

      requests.set(key, {
        key,
        leave,
        className,
        rangeStart: leaveRange?.start ?? leave.startDate,
        rangeEnd: leaveRange?.end ?? leave.endDate,
      });
    });

    const byEmployee = new Map<
      string,
      { id: string; name: string; requests: TimelineRequest[] }
    >();

    // Seed the roster first so everyone keeps a row, leave or not.
    employees?.forEach(({ id, name }) =>
      byEmployee.set(id, { id, name, requests: [] })
    );

    requests.forEach((req) => {
      const { id, firstName, lastName } = req.leave.employee;
      const entry = byEmployee.get(id);
      if (entry) {
        entry.requests.push(req);
        return;
      }
      // A roster is the definitive row set — an employee filtered out of it
      // stays out, even if events for them are still in flight from the
      // previous query. Without a roster, rows come from the leave itself.
      if (employees) return;
      byEmployee.set(id, {
        id,
        name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
        requests: [req],
      });
    });

    return [...byEmployee.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [events, employees]);

  /** Distinct people away per day — one person on two leaves still counts once. */
  const awayPerDay = useMemo(() => {
    const counts = days.map(() => new Set<string>());
    rows.forEach(({ id, requests }) => {
      requests.forEach((req) => {
        days.forEach((d, i) => {
          const dIso = toIso(d);
          if (
            dIso >= req.rangeStart &&
            dIso <= req.rangeEnd &&
            isWorkingDay(d)
          ) {
            counts[i].add(id);
          }
        });
      });
    });
    return counts.map((s) => s.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, days, holidays, nonWorkingDays]);

  const gridStyle = {
    gridTemplateColumns: `12rem repeat(${days.length}, minmax(1.625rem, 1fr))`,
  };
  const minWidth = `${12 + days.length * 1.75}rem`;

  /**
   * One marker per holiday, each confined to its own column. Spanning the name
   * across following columns reads better in isolation but collides the moment
   * two holidays fall close together, so the name lives in the tooltip.
   */
  const holidayMarkers = useMemo(
    () =>
      days
        .map((d, i) => ({ col: i + 2, name: holidays.get(toIso(d)) }))
        .filter((x): x is { col: number; name: string } => Boolean(x.name)),
    [days, holidays]
  );

  return (
    <div className={styles.scroll}>
      <div className={styles.inner} style={{ minWidth }}>
        <div className={styles.head}>
          {/* Day columns */}
          <div
            className={cn(styles.row, styles.daysRow)}
            style={gridStyle}
            role="row"
          >
            <span className={cn(styles.leadCell, styles.leadEyebrow)}>
              {t('calendar.timeline.employee')}
            </span>
            {days.map((d) => {
              const dIso = toIso(d);
              const holiday = holidays.get(dIso);
              return (
                <div
                  key={dIso}
                  title={holiday ?? ''}
                  className={cn(
                    styles.day,
                    isWeekend(d) && styles.dayWeekend,
                    holiday && styles.dayHoliday,
                    dIso === todayIso && styles.dayToday
                  )}
                >
                  <div className={styles.dayWd}>{weekdayLabel(d)}</div>
                  <div className={styles.dayNum}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* Holidays */}
          {holidayMarkers.length > 0 && (
            <div className={cn(styles.row, styles.metaRow)} style={gridStyle}>
              <span className={cn(styles.leadCell, styles.leadLabel)}>
                {t('calendar.timeline.holidays')}
              </span>
              {holidayMarkers.map((marker) => (
                <Tooltip key={marker.col} delayDuration={150}>
                  <TooltipTrigger asChild>
                    <span
                      className={styles.holMarker}
                      style={{ gridColumn: marker.col }}
                      tabIndex={0}
                      role="img"
                      aria-label={marker.name}
                    >
                      <CalendarDays aria-hidden="true" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{marker.name}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}

          {/* People away */}
          <div className={cn(styles.row, styles.metaRow)} style={gridStyle}>
            <span className={cn(styles.leadCell, styles.leadLabel)}>
              {t('calendar.timeline.people_away')}
            </span>
            {days.map((d, i) => {
              const dIso = toIso(d);
              const holiday = holidays.get(dIso);
              const count = awayPerDay[i];
              return (
                <div
                  key={dIso}
                  className={cn(
                    styles.away,
                    holiday && styles.awayHoliday,
                    !holiday && count >= 3 && styles.awayBusy
                  )}
                  title={
                    holiday ?? t('calendar.timeline.people_away_on', { count })
                  }
                >
                  {holiday
                    ? t('calendar.timeline.holiday_short')
                    : count || '·'}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.body}>
          {rows.map(({ id, name, requests }) => {
            return (
              <div key={id} className={styles.empRow} style={gridStyle}>
                <div className={styles.emp}>
                  <span
                    className={styles.avatar}
                    style={{ background: getColorForText(name, AVATAR_COLORS) }}
                  >
                    {initialsOf(name)}
                  </span>
                  <span className={styles.empName} title={name}>
                    {name}
                  </span>
                </div>

                {/* Backing cells */}
                {days.map((d, i) => {
                  const dIso = toIso(d);
                  const holiday = holidays.get(dIso);
                  return (
                    <div
                      key={dIso}
                      title={
                        holiday ??
                        (isWeekend(d)
                          ? t('calendar.timeline.non_working_day')
                          : '')
                      }
                      className={cn(
                        styles.cell,
                        isWeekend(d) && styles.cellWeekend,
                        holiday && styles.cellHoliday
                      )}
                      style={{ gridColumn: i + 2 }}
                    />
                  );
                })}

                {/* Bars + connectors */}
                {requests.map((req) => {
                  const built = buildSegments(
                    days,
                    isWorkingDay,
                    req.rangeStart,
                    req.rangeEnd
                  );
                  if (!built) return null;

                  const pending = req.leave.status === 'pending';
                  const typeName = req.leave.leaveType.name;

                  return (
                    <Fragment key={req.key}>
                      {built.segs.map((seg, k) => {
                        const contLeft =
                          k > 0 || req.rangeStart < toIso(days[seg.a]);
                        const contRight =
                          k < built.segs.length - 1 ||
                          req.rangeEnd > toIso(days[seg.b]);
                        const width = seg.b - seg.a + 1;

                        return (
                          <Tooltip key={`${req.key}-s${k}`} delayDuration={200}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={`${name}, ${typeName}`}
                                className={cn(
                                  styles.bar,
                                  pending && styles.barPending,
                                  req.className
                                )}
                                style={{
                                  gridColumn: `${seg.a + 2} / ${seg.b + 3}`,
                                  borderRadius: radius(contLeft, contRight),
                                }}
                              >
                                {width >= 3 ? typeName : shortCode(typeName)}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="p-0 border-none shadow-xl bg-white text-gray-900 pointer-events-none z-60"
                            >
                              <EventDetailCard
                                leave={req.leave}
                                date={req.rangeStart}
                                badgeClass={req.className}
                                range={{
                                  start: req.rangeStart,
                                  end: req.rangeEnd,
                                }}
                              />
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}

                      {built.conns.map((c, k) => (
                        <span
                          key={`${req.key}-c${k}`}
                          aria-hidden="true"
                          className={cn(styles.conn, req.className)}
                          style={{ gridColumn: `${c.a + 2} / ${c.b + 3}` }}
                        />
                      ))}
                    </Fragment>
                  );
                })}
              </div>
            );
          })}
          {footer}
        </div>
      </div>
    </div>
  );
}
